import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
	EyeIcon,
	EyeSlashIcon,
	LockClosedIcon,
	EnvelopeIcon,
} from '@heroicons/react/24/outline';

import { Button, Input, Card } from '../ui';
import { useAuth } from '../auth/AuthContext';
import { homePathFor } from '../auth/roles';

// Schema messages stay English — they're translated at render time in
// Phase 3 (#44) via Zod's setErrorMap, the proper place for it. Translating
// them via t() inside the schema would couple the schema to React context.
const schema = z.object({
	email: z.string().min(1, 'Email is required').email('Invalid email'),
	password: z.string().min(1, 'Password is required').max(128),
});
type FormValues = z.infer<typeof schema>;

export default function Login() {
	const { status, user, login } = useAuth();
	const { t } = useTranslation();
	const location = useLocation();
	const [serverError, setServerError] = React.useState<string | null>(null);
	const [showPwd, setShowPwd] = React.useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: { email: '', password: '' },
	});

	if (status === 'authenticated' && user) {
		const target =
			(location.state as any)?.from?.pathname || homePathFor(user.role);
		return <Navigate to={target} replace />;
	}

	const onSubmit = async (values: FormValues) => {
		setServerError(null);
		try {
			await login(values.email, values.password);
		} catch (err: unknown) {
			// Surface a localised, user-friendly error. The BE message (when
			// present) is already english; we prefer the localised key for
			// 401 ("Invalid credentials"). Phase 3 will let the BE return
			// `{ messageKey, params }` so we can translate any error.
			const status = (err as { response?: { status?: number } })?.response?.status;
			setServerError(
				status === 401
					? t('auth:login.errors.invalidCredentials')
					: t('auth:login.errors.generic')
			);
		}
	};

	return (
		<div className="min-h-screen flex bg-ground">
			{/* Decorative panel — desktop only */}
			<div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-brand-900 via-brand-800 to-brand-700 text-white p-12 flex-col justify-between relative overflow-hidden">
				<div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-accent-500/10 blur-3xl" />
				<div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-brand-400/10 blur-3xl" />

				<div className="relative flex items-center gap-3 text-2xl font-semibold tracking-tight">
					<div className="h-9 w-9 rounded-xl bg-white/10 grid place-items-center backdrop-blur-sm">
						<svg
							viewBox="0 0 24 24"
							className="h-5 w-5"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
						>
							<path
								d="M3 13h18l-2-7H5l-2 7zm0 0v6h18v-6M7 17h2m6 0h2"
								strokeLinecap="round"
							/>
						</svg>
					</div>
					<span>
						SPARTS <span className="font-light text-white/70">auto</span>
					</span>
				</div>

				<div className="relative">
					<h2 className="text-3xl xl:text-4xl font-semibold leading-tight">
						{t('auth:login.hero.title')}
					</h2>
					<p className="mt-4 text-white/70 max-w-md">
						{t('auth:login.hero.subtitle')}
					</p>
					<dl className="mt-10 grid grid-cols-3 gap-6 text-white/80 text-sm">
						<div>
							<dt className="text-2xl font-semibold text-white">3×</dt>
							<dd>{t('auth:login.hero.stats.speed')}</dd>
						</div>
						<div>
							<dt className="text-2xl font-semibold text-white">42</dt>
							<dd>{t('auth:login.hero.stats.quotes')}</dd>
						</div>
						<div>
							<dt className="text-2xl font-semibold text-white">24/7</dt>
							<dd>{t('auth:login.hero.stats.delivery')}</dd>
						</div>
					</dl>
				</div>

				<p className="relative text-xs text-white/40">
					{t('auth:login.hero.copyright')}
				</p>
			</div>

			{/* Form panel */}
			<div className="flex-1 flex items-center justify-center p-4 sm:p-8">
				<div className="w-full max-w-md">
					{/* Mobile brand mark */}
					<div className="lg:hidden flex items-center gap-2 text-ink mb-8">
						<div className="h-9 w-9 rounded-xl bg-brand-800 grid place-items-center text-white">
							<svg
								viewBox="0 0 24 24"
								className="h-5 w-5"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<path
									d="M3 13h18l-2-7H5l-2 7zm0 0v6h18v-6M7 17h2m6 0h2"
									strokeLinecap="round"
								/>
							</svg>
						</div>
						<div className="text-xl font-semibold tracking-tight">
							SPARTS <span className="text-ink-muted font-light">auto</span>
						</div>
					</div>

					<Card padding="lg" className="!shadow-card">
						<h1 className="text-2xl font-semibold text-ink">
							{t('auth:login.welcomeBack')}
						</h1>
						<p className="text-sm text-ink-muted mt-1">{t('auth:login.subtitle')}</p>

						<form
							className="mt-6 space-y-4"
							onSubmit={handleSubmit(onSubmit)}
							noValidate
						>
							<Input
								label={t('auth:login.email')}
								type="email"
								autoComplete="username"
								required
								leftIcon={<EnvelopeIcon className="h-4 w-4" />}
								placeholder={t('auth:login.emailPlaceholder')}
								error={errors.email?.message}
								{...register('email')}
							/>
							<Input
								label={t('auth:login.password')}
								type={showPwd ? 'text' : 'password'}
								autoComplete="current-password"
								required
								leftIcon={<LockClosedIcon className="h-4 w-4" />}
								rightIcon={
									<button
										type="button"
										onClick={() => setShowPwd((v) => !v)}
										className="text-ink-muted hover:text-ink"
										aria-label={
											showPwd ? t('auth:login.hidePassword') : t('auth:login.showPassword')
										}
									>
										{showPwd ? (
											<EyeSlashIcon className="h-4 w-4" />
										) : (
											<EyeIcon className="h-4 w-4" />
										)}
									</button>
								}
								placeholder={t('auth:login.passwordPlaceholder')}
								error={errors.password?.message}
								{...register('password')}
							/>

							{serverError && (
								<div
									role="alert"
									className="rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-800"
								>
									{serverError}
								</div>
							)}

							<div className="flex items-center justify-between">
								<label className="flex items-center gap-2 text-sm text-ink-muted">
									<input
										type="checkbox"
										className="h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-500"
									/>
									{t('auth:login.rememberMe')}
								</label>
								<Link
									to="/forgot-password"
									className="text-sm text-brand-700 hover:text-brand-800 font-medium"
								>
									{t('auth:login.forgotPassword')}
								</Link>
							</div>

							<Button type="submit" fullWidth size="lg" loading={isSubmitting}>
								{isSubmitting ? t('auth:login.submitting') : t('auth:login.submit')}
							</Button>
						</form>
					</Card>

					<p className="mt-6 text-xs text-ink-muted text-center">
						{t('auth:login.terms')}
					</p>
				</div>
			</div>
		</div>
	);
}
